import { createContext, useContext, useEffect, useMemo, useState } from 'react';

import {
    RequestMethod,
    useServerErrorEffect,
    useServerSuccessEffect,
    useServerWrite,
} from 'dg-web-shared/lib/hooks/ServerStateHooks';
import { getParameterByName } from 'dg-web-shared/lib/Url';

export enum AuthStatusType {
    UNKNOWN,
    OK,
    NOT_OK,
}
export interface AuthState {
    authStatus: AuthStatusType;
    setAuthStatus: (status: AuthStatusType) => void;
}

const AppStateContext = createContext<AuthState>({
    authStatus: AuthStatusType.UNKNOWN,
    setAuthStatus: () => null,
});

export function useAuthState() {
    return useContext(AppStateContext);
}

let authCheckTriggered = false;

function getAuthHeaders() {
    const token = getParameterByName(window.location.href, 'token');
    return token
        ? {
              Authorization: `Bearer ${token}`,
          }
        : undefined;
}

export function AuthStateProvider({ children }: { children: React.ReactNode }) {
    const authHeaders = getAuthHeaders();
    const [authState, fetchAuthState] = useServerWrite<null, null, null>(
        () => ({
            url: '/ui-api/customer-account/auth-token-validity-v2',
            method: RequestMethod.GET,
            headers: authHeaders ?? undefined,
        }),
    );
    const [authStatus, setAuthStatus] = useState<AuthStatusType>(
        AuthStatusType.UNKNOWN,
    );

    useEffect(() => {
        if (authStatus !== AuthStatusType.UNKNOWN || authCheckTriggered) {
            return;
        }
        authCheckTriggered = true;
        fetchAuthState(null);
    }, [authStatus, fetchAuthState]);

    useServerSuccessEffect(authState, () => {
        setAuthStatus(AuthStatusType.OK);
    });

    useServerErrorEffect(authState, () => {
        setAuthStatus(AuthStatusType.NOT_OK);
    });

    const appStateContextValue = useMemo(
        () => ({
            authStatus,
            setAuthStatus,
        }),
        [authStatus],
    );

    return (
        <AppStateContext.Provider value={appStateContextValue}>
            {children}
        </AppStateContext.Provider>
    );
}
